/**
 * Frasi note per i codici del servizio, nella lingua della scheda.
 *
 * Il corpo di un errore API resta italiano: e' il servizio che lo scrive, e
 * tradurlo a occhio inventerebbe una seconda fonte. I codici invece sono
 * stabili — `lease_lost` non cambia lingua — e la scheda puo' dirli con una
 * voce del catalogo. Un codice sconosciuto continua a mostrare il messaggio
 * ricevuto: meglio una frase vera in italiano che una traduzione inventata.
 */
import type { MessageKey } from './messages'

/* Ogni codice che una persona puo' incontrare lavorando come si deve — un
 * conflitto di stato, non un errore di contratto — ha qui la sua frase. I
 * codici di contratto (422 su campi che la scheda non manda, 404 su indirizzi
 * che la scheda non compone) restano fuori per scelta, e `tests/
 * test_explained_codes.py` tiene l'elenco allineato al servizio (GI-281). */
const CODES: Record<string, MessageKey> = {
  lease_lost: 'error.leaseLost',
  lease_not_yours: 'error.leaseNotYours',
  lease_candidate_mismatch: 'error.leaseMismatch',
  lease_limit_reached: 'error.leaseLimit',
  candidate_leased: 'error.candidateLeased',
  candidate_already_pending: 'error.candidatePending',
  coverage_already_pending: 'error.coveragePending',
  session_expired: 'error.sessionExpired',
  session_closed: 'error.sessionClosed',
  intent_stale: 'error.intentStale',
  intent_rejected: 'error.intentRejected',
  stale_epoch: 'error.staleEpoch',
  version_conflict: 'error.versionConflict',
  coverage_conflict: 'error.coverageConflict',
  review_frozen: 'error.reviewFrozen',
  not_yours: 'error.notYours',
  nothing_to_undo: 'error.nothingToUndo',
  undo_deadline_elapsed: 'error.undoElapsed',
  shadow_prediction: 'error.shadowPrediction',
  freeze_blocked: 'error.freezeBlocked',
  coverage_incomplete: 'error.coverageIncomplete',
  coverage_pending: 'error.coverageInFlight',
  not_frozen: 'error.notFrozen',
  empty_population: 'error.emptyPopulation',
  sample_incomplete: 'error.sampleIncomplete',
  sample_not_current: 'error.sampleNotCurrent',
  not_in_sample: 'error.notInSample',
  seal_blocked: 'error.sealBlocked',
  seal_missing: 'error.sealMissing',
  seal_stale: 'error.sealStale',
  seal_mismatch: 'error.sealStale',
  no_active_catalog: 'error.noActiveCatalog',
  invalidated: 'error.deliveryInvalidated',
  already_submitted: 'error.alreadySubmitted',
  not_submitted: 'error.notSubmitted',
  note_required: 'error.noteRequired',
  reason_required: 'error.reasonRequired',
  reference_required: 'error.referenceRequired',
  metrics_not_met: 'error.metricsNotMet',
  last_manager: 'error.lastManager',
}

export function explainService(
  t: (key: MessageKey) => string,
  code: string | null | undefined,
  fallback: string,
): string {
  if (code === null || code === undefined) return fallback
  const key = CODES[code]
  return key === undefined ? fallback : t(key)
}

/**
 * I motivi di rifiuto, nella lingua della scheda.
 *
 * L'elenco vive nella base dati con la sua etichetta italiana — e' quello che
 * il servizio conosce e che sta nei verdetti. La scheda inglese pero' li
 * mostrava tali e quali, cinque tasti in italiano in mezzo a una pagina
 * tradotta. Il codice e' stabile, quindi la scheda lo dice con una voce del
 * catalogo; un motivo aggiunto in base dati e non ancora nel catalogo
 * continua a mostrare l'etichetta ricevuta, che e' vera anche se non tradotta.
 */
const REASONS: Record<string, MessageKey> = {
  non_anomalia: 'reason.nonAnomalia',
  altro_tipo: 'reason.altroTipo',
  duplicato: 'reason.duplicato',
  illeggibile: 'reason.illeggibile',
  elemento_errato: 'reason.elementoErrato',
}

export function explainReason(
  t: (key: MessageKey) => string,
  code: string | null | undefined,
  fallback: string,
): string {
  if (code === null || code === undefined) return fallback
  const key = REASONS[code]
  return key === undefined ? fallback : t(key)
}

/**
 * Chi puo' fare cio' che qui non si puo' fare.
 *
 * Le schermate dicevano all'operatore «per sbloccare serve `quality.sample`»:
 * il nome interno di un permesso, in mezzo a una frase italiana. Chi legge non
 * puo' farci niente — non e' una cosa che si ottiene, e' una cosa che si
 * chiede a qualcuno. La frase nomina quel qualcuno; il codice resta accanto,
 * per chi deve capire il perche' — la stessa divisione dei rifiuti del banco,
 * dove la frase serve a chi lavora e il codice a chi indaga.
 */
const WHO: Record<string, MessageKey> = {
  'ingest.retry': 'who.uploader',
  'ingest.read': 'who.uploader',
  'package.upload': 'who.uploader',
  'review.queue': 'who.qa',
  'review.decide': 'who.qa',
  'finding.manual': 'who.qa',
  'review.freeze': 'who.supervisor',
  'quality.sample': 'who.supervisor',
  'quality.seal': 'who.supervisor',
  'export.compose': 'who.supervisor',
  'export.send': 'who.supervisor',
  'audit.read': 'who.manager',
  'user.manage': 'who.manager',
  'settings.manage': 'who.manager',
  'catalog.import': 'who.manager',
}

export function whoCan(t: (key: MessageKey) => string, capability: string): string {
  const key = WHO[capability]
  // Un permesso che non sta nella tabella si mostra com'e': meglio un codice
  // che una persona sbagliata.
  return key === undefined ? capability : t(key)
}

/**
 * Le parole del prodotto che non hanno un equivalente quotidiano.
 *
 * **Ogni voce dice che cosa cambia per chi legge, non che cos'e'.** Un
 * dizionario risponde alla domanda «che cos'e' questo»; chi sta lavorando ha
 * un'altra domanda, ed e' «e allora io che faccio». «Prenotazione: meccanismo
 * di blocco temporaneo di novanta secondi» e' esatto e inutile; «questo
 * candidato e' tuo per novanta secondi, se ti allontani torna in coda ed e'
 * normale» e' la stessa cosa detta a una persona.
 *
 * L'elenco e' chiuso e vive qui perche' queste parole cambiano insieme al
 * prodotto — cioe' insieme al codice — e vanno corrette dallo stesso commit
 * che cambia il comportamento. Una tabella modificabile nella base dati si
 * separerebbe dal prodotto senza che nessuno se ne accorga.
 */
export const CONCEPTS = [
  'lease',
  'rank',
  'margin',
  'outbox',
  'intent',
  'snapshot',
  'freeze',
  'sample',
  'seal',
  'delivery',
  'catalogEdition',
  'hypothesis',
  'critical',
] as const

export type Concept = (typeof CONCEPTS)[number]

export function conceptTerm(t: (key: MessageKey) => string, about: Concept): string {
  return t(`concept.${about}.term` as MessageKey)
}

export function conceptSays(t: (key: MessageKey) => string, about: Concept): string {
  return t(`concept.${about}.says` as MessageKey)
}

/**
 * Il glossario, nell'ordine in cui le parole si incontrano lavorando.
 *
 * **Non alfabetico.** L'alfabeto serve a chi conosce la parola e ne cerca il
 * senso; qui e' piu' spesso il contrario — si e' visto il processo e non si sa
 * come si chiama cio' che si e' visto. Le sezioni seguono il percorso di un
 * pacchetto: prima il banco dell'operatore, poi il cancello di qualita', poi
 * la consegna, poi cio' che tiene in piedi il resto.
 *
 * E' una vista sullo stesso dizionario delle spiegazioni, non un secondo
 * testo: una voce che qui dicesse una cosa diversa da quella che dice la
 * spiegazione accanto alla parola sarebbe una bugia in una delle due.
 */
export const GLOSSARY: { section: MessageKey; concepts: Concept[] }[] = [
  { section: 'glossary.operator', concepts: ['lease', 'rank', 'margin', 'outbox', 'intent', 'snapshot'] },
  { section: 'glossary.quality', concepts: ['freeze', 'sample', 'seal'] },
  { section: 'glossary.delivery', concepts: ['delivery', 'catalogEdition'] },
  { section: 'glossary.admin', concepts: ['hypothesis', 'critical'] },
]
